import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n, intlTag } from "@/i18n";
import { Th, Td } from "@/components/common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, MinusCircle, AlertTriangle } from "lucide-react";

const ALL = "__all__";

const STATUS = {
  final: { key: "canc.statusFinal", cls: "text-primary", Icon: CheckCircle2 },
  voided: { key: "canc.statusVoided", cls: "text-muted-foreground", Icon: MinusCircle },
  provisional: { key: "canc.statusProvisional", cls: "text-destructive", Icon: AlertTriangle },
};

/** Nachtrag Abschnitt D: stornierte Aufträge mit trotzdem angefallenen Versandkosten. */
export default function CancelledShippingCosts() {
  const { t, locale } = useI18n();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [carrier, setCarrier] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [data, setData] = useState(null);

  const money = (v, cur) => new Intl.NumberFormat(intlTag(locale), {
    style: "currency", currency: cur || "EUR" }).format(Number(v || 0));

  const load = useCallback(() => {
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    if (carrier !== ALL) params.carrier = carrier;
    if (status !== ALL) params.status = status;
    api.get("/finance/cancelled-shipping-costs", { params })
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [from, to, carrier, status]);
  useEffect(() => { load(); }, [load]);

  const rows = data?.rows || [];

  return (
    <div className="space-y-4" data-testid="cancelled-shipping-costs">
      <div>
        <h3 className="font-heading text-lg font-medium">{t("canc.title")}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{t("canc.subtitle")}</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div><Label>{t("common.from")}</Label>
          <Input type="date" className="mt-1.5 w-40" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="canc-from" /></div>
        <div><Label>{t("common.to")}</Label>
          <Input type="date" className="mt-1.5 w-40" value={to} onChange={(e) => setTo(e.target.value)} data-testid="canc-to" /></div>
        <div><Label>{t("ship.carrier")}</Label>
          <Select value={carrier} onValueChange={setCarrier}>
            <SelectTrigger className="mt-1.5 w-44" data-testid="canc-carrier"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("canc.allCarriers")}</SelectItem>
              {(data?.carriers || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>{t("common.status")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="mt-1.5 w-56" data-testid="canc-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("canc.allStatus")}</SelectItem>
              <SelectItem value="final">{t("canc.statusFinal")}</SelectItem>
              <SelectItem value="provisional">{t("canc.statusProvisional")}</SelectItem>
              <SelectItem value="voided">{t("canc.statusVoided")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {data && rows.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="canc-empty">{t("canc.empty")}</p>
      )}

      {rows.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50"><tr>
                <Th>{t("ord.title")}</Th><Th>{t("canc.cancelledAt")}</Th>
                <Th>{t("ship.shipment")}</Th><Th>{t("ship.carrier")}</Th>
                <Th>{t("ship.documentDate")}</Th>
                <Th right>{t("common.net")}</Th><Th right>{t("common.tax")}</Th>
                <Th right>{t("common.gross")}</Th><Th>{t("common.currency")}</Th>
                <Th>{t("ship.costStatus")}</Th><Th>{t("common.status")}</Th>
                <Th>{t("ship.carrierInvoiceRef")}</Th>
              </tr></thead>
              <tbody>
                {rows.map((r) => {
                  const s = STATUS[r.status];
                  return (
                    <tr key={r.fulfillment_id} className="border-t border-border" data-testid={`canc-row-${r.fulfillment_id}`}>
                      <Td>{r.order_number || "—"}</Td>
                      <Td>{r.cancelled_at || "—"}</Td>
                      <Td mono className="text-xs">{r.fulfillment_id.slice(0, 8)}</Td>
                      <Td>{r.carrier || "—"}</Td>
                      <Td>{r.document_date || "—"}</Td>
                      <Td right mono>{money(r.net, r.currency)}</Td>
                      <Td right mono>{money(r.tax_amount, r.currency)}</Td>
                      <Td right mono>{money(r.gross, r.currency)}</Td>
                      <Td>{r.currency}</Td>
                      <Td mono className="text-xs">{r.cost_status || "—"}</Td>
                      <Td>
                        <span className={`inline-flex items-center gap-1.5 text-xs ${s.cls}`}>
                          <s.Icon className="h-3.5 w-3.5" />{t(s.key)}
                        </span>
                      </Td>
                      <Td className="text-xs text-muted-foreground">{r.carrier_invoice_reference || "—"}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap gap-3" data-testid="canc-totals">
            {(data.totals || []).map((tt) => (
              <div key={tt.currency} className="rounded-lg border border-border p-4 text-sm">
                <div className="text-xs text-muted-foreground">{tt.currency}</div>
                <div className="mt-1 flex justify-between gap-6">
                  <span>{t("canc.expenseTotal")}</span>
                  <span className="font-mono font-medium">{money(tt.expense_net, tt.currency)}</span>
                </div>
                <div className="mt-1 flex justify-between gap-6 text-muted-foreground">
                  <span>{t("canc.neutralisedTotal")}</span>
                  <span className="font-mono">{money(tt.neutralised_net, tt.currency)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
